'use client'

import { memo, useEffect, useRef, useState } from 'react'
import Script from 'next/script'
import { MIN_HEADER_HEIGHT } from '@/lib/config'

// Next owns the container; crate-front owns everything inside it after mounting.
const HeaderRoot = memo(function HeaderRoot({ markup }: { markup: string }) {
  return (
    <div
      id="header-root"
      data-new-url-scheme="1"
      data-search-callback="pcSearchCallback"
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  )
})

export function HeaderRuntime({ markup, scriptUrl }: { markup: string; scriptUrl: string }) {
  // Keep the original markup stable even if a parent refreshes after crate-front mounts.
  const initialMarkup = useRef(markup)
  const [canLoadScript, setCanLoadScript] = useState(false)

  useEffect(() => {
    const root = document.getElementById('header-root')
    if (!root) return

    let header: Element | null = null
    let previousHeight = 0
    const syncHeight = () => {
      const height = Math.max(MIN_HEADER_HEIGHT, header?.getBoundingClientRect().height ?? 0)
      if (height !== previousHeight) {
        document.documentElement.style.setProperty('--header-height', `${height}px`)
        previousHeight = height
      }
    }
    const resizeObserver = new ResizeObserver(syncHeight)
    const observeHeader = () => {
      const nextHeader = root.querySelector('.header')
      if (nextHeader !== header) {
        if (header) resizeObserver.unobserve(header)
        header = nextHeader
        if (header) resizeObserver.observe(header)
      }
      syncHeight()
    }
    const mutationObserver = new MutationObserver(observeHeader)
    mutationObserver.observe(root, { childList: true, subtree: true })
    observeHeader()

    // The external React runtime must not replace markup until Next has hydrated this island.
    setCanLoadScript(true)

    return () => {
      mutationObserver.disconnect()
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <>
      <HeaderRoot markup={initialMarkup.current} />
      {canLoadScript && (
        <Script
          id="crate-front-header"
          src={scriptUrl}
          strategy="afterInteractive"
          onLoad={() => window.dispatchEvent(new Event('productioncrate:header-ready'))}
          onError={() => console.error('The interactive header could not load; keeping the SSR header visible.')}
        />
      )}
    </>
  )
}
